import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCandidate } from '@/hooks/useCandidate';
import { useUpdateCandidateStatus } from '@/hooks/useUpdateCandidateStatus';
import { useInterviews } from '@/hooks/useInterviews';
import { useJob } from '@/hooks/useJobs';
import { useAuth } from '@/contexts/AuthContext';
import {
  CandidateStatus,
  VALID_STATUS_TRANSITIONS,
  QuestionCategory,
  InterviewQuestion,
  UserRole,
} from '@recruitai/shared';
import { AppLayout } from '@/components/AppLayout';
import { ExportPdfButton } from '@/components/pdf/ExportPdfButton';
import { generateFileName } from '@/utils/pdf-utils';
import { EmailModal } from '@/components/pipeline/EmailModal';

const STATUS_CONFIG: Record<CandidateStatus, { label: string; variant: 'warning' | 'default' | 'success' | 'destructive' | 'secondary' }> = {
  [CandidateStatus.PENDING]: { label: 'Pendente', variant: 'warning' },
  [CandidateStatus.ANALYZING]: { label: 'Analisando', variant: 'default' },
  [CandidateStatus.APPROVED]: { label: 'Aprovado', variant: 'success' },
  [CandidateStatus.REJECTED]: { label: 'Rejeitado', variant: 'destructive' },
  [CandidateStatus.INTERVIEWED]: { label: 'Entrevistado', variant: 'secondary' },
  [CandidateStatus.HIRED]: { label: 'Contratado', variant: 'success' },
};

const STATUS_ACTION_LABELS: Partial<Record<CandidateStatus, string>> = {
  [CandidateStatus.APPROVED]: 'Aprovar',
  [CandidateStatus.REJECTED]: 'Rejeitar',
  [CandidateStatus.INTERVIEWED]: 'Marcar Entrevistado',
  [CandidateStatus.HIRED]: 'Contratar',
};

const CATEGORY_LABELS: Record<QuestionCategory, string> = {
  [QuestionCategory.TECHNICAL]: 'Técnicas',
  [QuestionCategory.BEHAVIORAL]: 'Comportamentais',
  [QuestionCategory.SITUATIONAL]: 'Situacionais',
  [QuestionCategory.CULTURE]: 'Cultura',
};

const SCORE_LABELS: Record<string, string> = {
  technical: 'Técnico',
  experience: 'Experiência',
  culture: 'Cultura',
  communication: 'Comunicação',
};

export default function CandidateDetailPage() {
  const { jobId, candidateId } = useParams<{ jobId: string; candidateId: string }>();
  const navigate = useNavigate();
  const { candidate, resumeDownloadUrl, isLoading, error, refetch, setCandidate } = useCandidate(jobId!, candidateId!);
  const { updateStatus, isUpdating } = useUpdateCandidateStatus(jobId!, candidateId!);
  const { job } = useJob(jobId!);
  const { user } = useAuth();
  const fitScoreRef = useRef<HTMLDivElement>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const canSendEmail = user?.role && [UserRole.OWNER, UserRole.ADMIN, UserRole.RECRUITER].includes(user.role as UserRole);

  const handleStatusChange = async (newStatus: CandidateStatus) => {
    const updated = await updateStatus(newStatus);
    if (updated) {
      setCandidate(updated);
      toast.success('Status atualizado com sucesso!');
    }
  };
  const { interviews } = useInterviews(jobId!, candidateId!);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando candidato...</p>
        </div>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive mb-4">Candidato não encontrado</p>
            <Button onClick={() => navigate(`/jobs/${jobId}/candidates`)}>Voltar</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[candidate.status];
  const transitions = VALID_STATUS_TRANSITIONS[candidate.status] ?? [];
  const fitReport = candidate.fitReport;
  const questions = candidate.interviewQuestions ?? [];

  // Group questions by category
  const grouped = questions.reduce<Record<string, InterviewQuestion[]>>((acc, q) => {
    (acc[q.category] ??= []).push(q);
    return acc;
  }, {});

  const isAnalyzing = candidate.status === CandidateStatus.ANALYZING;

  return (
    <AppLayout
      actions={
        <Button variant="outline" onClick={() => navigate(`/jobs/${jobId}/candidates`)}>Voltar</Button>
      }
    >
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header info */}
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold mb-1">{candidate.name}</h2>
            <p className="text-muted-foreground">{candidate.email}</p>
            {candidate.phone && <p className="text-muted-foreground">📞 {candidate.phone}</p>}
            {candidate.city && <p className="text-muted-foreground">📍 {candidate.city}</p>}
            <p className="text-sm text-muted-foreground mt-1">
              Enviado em {new Date(candidate.uploadedAt).toLocaleDateString('pt-BR')}
              {candidate.analyzedAt && ` • Analisado em ${new Date(candidate.analyzedAt).toLocaleDateString('pt-BR')}`}
              {candidate.source === 'career_page' && candidate.applicationDate && ` • Candidatura em ${new Date(candidate.applicationDate).toLocaleDateString('pt-BR')}`}
            </p>
            {candidate.source && (
              <Badge variant={candidate.source === 'career_page' ? 'default' : 'secondary'} className="mt-2">
                {candidate.source === 'career_page' ? 'Candidatura Espontânea' : candidate.source === 'batch_upload' ? 'Upload em Lote' : 'Upload Manual'}
              </Badge>
            )}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Badge variant={statusCfg.variant} className="text-sm px-3 py-1">{statusCfg.label}</Badge>
            {resumeDownloadUrl && (
              <a href={resumeDownloadUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">📄 Download Currículo</Button>
              </a>
            )}
          </div>
        </div>

        {/* Resume preview */}
        {resumeDownloadUrl && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Currículo</CardTitle>
            </CardHeader>
            <CardContent>
              <iframe
                src={resumeDownloadUrl}
                title="Visualização do currículo"
                className="w-full rounded border"
                style={{ height: '600px' }}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Caso o preview não carregue, use o botão de download acima.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Cover Letter */}
        {candidate.coverLetter && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Carta de Apresentação</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{candidate.coverLetter}</p>
            </CardContent>
          </Card>
        )}

        {/* Analyzing spinner */}
        {isAnalyzing && (
          <Card className="mb-6">
            <CardContent className="py-8 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
              <p className="text-muted-foreground">Currículo em análise... Isso pode levar alguns minutos.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>Atualizar</Button>
            </CardContent>
          </Card>
        )}

        {/* Fit Score + Category Scores */}
        {fitReport && (
          <Card className="mb-6" ref={fitScoreRef}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="flex items-center gap-3">
                  Fit Score: {fitReport.fitScore}%
                  <Badge variant={fitReport.recommendation === 'approved' ? 'success' : 'destructive'}>
                    {fitReport.recommendation === 'approved' ? 'Aprovado' : 'Rejeitado'}
                  </Badge>
                </CardTitle>
                <CardDescription>Análise de compatibilidade com a vaga</CardDescription>
              </div>
              <ExportPdfButton
                contentRef={fitScoreRef as React.RefObject<HTMLElement>}
                fileName={generateFileName('fit', candidate.name)}
                header={{
                  candidateName: candidate.name,
                  candidateEmail: candidate.email,
                  date: candidate.analyzedAt
                    ? new Date(candidate.analyzedAt).toLocaleDateString('pt-BR')
                    : '',
                  jobTitle: job?.title,
                }}
                disabled={!fitReport}
                tooltipText="Relatório de fit não disponível"
              />
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Category score bars */}
              <div className="space-y-3">
                {Object.entries(fitReport.scores).map(([key, value]) => (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{SCORE_LABELS[key] ?? key}</span>
                      <span className="font-medium">{value}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full ${value >= 70 ? 'bg-green-500' : value >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${value}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Strengths & Gaps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <h4 className="font-medium text-green-700 mb-2">✓ Pontos Fortes</h4>
                  <ul className="space-y-1">
                    {fitReport.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-muted-foreground">• {s}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-red-700 mb-2">✗ Lacunas</h4>
                  <ul className="space-y-1">
                    {fitReport.gaps.map((g, i) => (
                      <li key={i} className="text-sm text-muted-foreground">• {g}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Justification */}
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-medium mb-2">Justificativa</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{fitReport.justification}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Interview Questions */}
        {questions.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Perguntas de Entrevista</CardTitle>
              <CardDescription>{questions.length} perguntas sugeridas pela IA</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(grouped).map(([cat, qs]) => (
                <div key={cat}>
                  <h4 className="font-medium mb-3">
                    {CATEGORY_LABELS[cat as QuestionCategory] ?? cat} ({qs.length})
                  </h4>
                  <div className="space-y-4">
                    {qs.map((q, i) => (
                      <div key={q.id || i} className="bg-gray-50 rounded-lg p-4">
                        <p className="font-medium text-sm mb-2">{q.question}</p>
                        <p className="text-xs text-muted-foreground mb-1">
                          <span className="font-medium">Razão:</span> {q.rationale}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium">O que observar:</span> {q.whatToLookFor}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Processing error / rejection reason */}
        {candidate.rejectionReason && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>
                {candidate.status === CandidateStatus.PENDING ? 'Erro no Processamento' : 'Motivo da Rejeição'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{candidate.rejectionReason}</p>
            </CardContent>
          </Card>
        )}

        {/* Entrevistas Realizadas */}
        {interviews.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Entrevistas Realizadas</CardTitle>
              <CardDescription>{interviews.length} entrevista{interviews.length > 1 ? 's' : ''}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {interviews.map((iv) => (
                <div
                  key={iv.interviewId}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => navigate(`/jobs/${jobId}/candidates/${candidateId}/interviews/${iv.interviewId}`)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{iv.mode === 'audio' ? '🎙️' : '📝'}</span>
                    <div>
                      <p className="text-sm font-medium">
                        Entrevista {iv.mode === 'audio' ? 'por Áudio' : 'por Texto'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(iv.conductedAt).toLocaleDateString('pt-BR')} • Score: {iv.finalScore ?? '—'}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">Ver Resultado →</Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Status actions */}
        {transitions.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Ações</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-2 flex-wrap">
                {canSendEmail && (
                  <Button variant="outline" onClick={() => setShowEmailModal(true)}>
                    ✉️ Enviar Email
                  </Button>
                )}
                {(candidate.status === CandidateStatus.APPROVED || candidate.status === CandidateStatus.INTERVIEWED) && (
                  <Button
                    variant="default"
                    onClick={() => navigate(`/jobs/${jobId}/candidates/${candidateId}/interview`)}
                  >
                    🎤 Iniciar Entrevista
                  </Button>
                )}
                {transitions.map(t => (
                  <Button
                    key={t}
                    variant={t === CandidateStatus.REJECTED ? 'destructive' : 'outline'}
                    disabled={isUpdating}
                    onClick={() => handleStatusChange(t)}
                  >
                    {STATUS_ACTION_LABELS[t] ?? t}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Email History */}
        {(candidate as any).emailHistory?.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Histórico de Emails</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {((candidate as any).emailHistory || []).map((record: any, i: number) => (
                  <div key={record.emailId || i} className="text-sm border-b pb-2 last:border-0">
                    <div className="flex justify-between">
                      <span className="font-medium">{record.subject}</span>
                      <span className="text-muted-foreground text-xs">{new Date(record.sentAt).toLocaleDateString('pt-BR')}</span>
                    </div>
                    <p className="text-muted-foreground text-xs mt-1">{record.body}</p>
                    <span className="text-muted-foreground text-xs">Por: {record.senderName} · Etapa: {record.stageName}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Email Modal */}
        {showEmailModal && candidate && (
          <EmailModal
            open={showEmailModal}
            onClose={() => { setShowEmailModal(false); refetch(); }}
            jobId={jobId!}
            candidateId={candidateId!}
            candidateName={candidate.name}
            jobTitle={job?.title || ''}
            stageName=""
            orgName=""
            emailHistory={(candidate as any).emailHistory}
          />
        )}
      </div>
    </AppLayout>
  );
}
